// Inventory Alpine.js component

import { inventoryService } from './inventory.service.js';
import { playUseSound, playEquipSound, playDiscardSound, playErrorSound } from '../utils/sounds.js';

export const inventory = () => ({
  // State
  slots: [],
  equipped: {},
  stats: {},
  loading: false,
  error: null,
  selectedSlot: null,
  showConfirmDialog: false,
  confirmAction: null,
  marketFactors: null,
  isOffline: false,
  
  // Initialize component
  init() {
    console.log('Inventory component initialized');
    this.loadInventory();
    this.loadMarketFactors();
    
    // Check if offline
    this.isOffline = !navigator.onLine;
    window.addEventListener('online', () => {
      this.isOffline = false;
      this.loadMarketFactors();
    });
    window.addEventListener('offline', () => {
      this.isOffline = true;
    });
    
    // Listen for inventory state changes
    window.addEventListener('inventory:state-changed', () => {
      this.loadInventory();
    });
  },
  
  // Load inventory data
  async loadInventory() {
    this.loading = true;
    this.error = null;
    
    try {
      // Try cache first if offline
      if (this.isOffline) {
        const cached = await this.getCachedInventory();
        if (cached) {
          this.slots = cached.slots;
          this.equipped = cached.equipped;
          this.stats = cached.stats;
          console.log('Inventory loaded from cache');
          return;
        }
      }
      
      this.slots = inventoryService.getSlots();
      this.equipped = inventoryService.getEquipped();
      this.stats = inventoryService.getStats();
      
      // Cache for offline use
      this.cacheInventory({ slots: this.slots, equipped: this.equipped, stats: this.stats });
      
      console.log(`Inventory loaded: ${this.stats.slotsUsed}/${this.stats.totalSlots} slots, ${this.stats.currentWeight}/${this.stats.maxWeight}kg`);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      
      // Try cache fallback
      try {
        const cached = await this.getCachedInventory();
        if (cached) {
          this.slots = cached.slots;
          this.equipped = cached.equipped;
          this.stats = cached.stats;
          this.error = 'Usando datos sin conexión';
          return;
        }
      } catch (cacheError) {
        console.warn('Cache fallback failed:', cacheError);
      }
      
      this.error = 'Error al cargar el inventario';
    } finally {
      this.loading = false;
    }
  },
  
  // Load market factors from API
  async loadMarketFactors() {
    try {
      const response = await this.$store.api.get('/economy/today');
      this.marketFactors = response.factors;
    } catch (error) {
      console.error('Failed to load market factors:', error);
    }
  },
  
  // Get item data for a slot
  getItemData(slot) {
    if (!slot) return null;
    return inventoryService.getItemData(slot.itemId);
  },
  
  // Get item display info
  getItemInfo(slot) {
    if (!slot) return null;
    
    const itemData = this.getItemData(slot);
    if (!itemData) return null;
    
    const rarity = inventoryService.getRarityData(itemData.rarity);
    
    return {
      ...itemData,
      quantity: slot.quantity,
      durability: slot.durability,
      rarity,
      totalWeight: Math.round(itemData.weight * slot.quantity * 10) / 10,
      totalValue: itemData.value * slot.quantity
    };
  },
  
  // Use item (consume)
  async useItem(slotIndex) {
    if (this.loading || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const slot = this.slots[slotIndex];
      if (!slot) return;
      
      const itemData = this.getItemData(slot);
      if (!itemData || itemData.category !== 'consumable') {
        throw new Error('Este item no se puede usar');
      }
      
      // Use item
      const usedItem = inventoryService.useItem(slotIndex);
      
      // Play sound effect
      await playUseSound();
      
      // Show success notification
      let effectsText = '';
      if (usedItem.effects) {
        const effects = Object.entries(usedItem.effects)
          .map(([stat, value]) => `+${value} ${this.getStatName(stat)}`)
          .join(', ');
        effectsText = ` (${effects})`;
      }
      
      this.$store.app.addNotification(
        `Usado: ${usedItem.name}${effectsText}`,
        'success'
      );
      
      this.loadInventory();
      
    } catch (error) {
      console.error('Failed to use item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },
  
  // Equip item
  async equipItem(slotIndex) {
    if (this.loading || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const slot = this.slots[slotIndex];
      if (!slot) return;
      
      const itemData = this.getItemData(slot);
      if (!itemData || itemData.category !== 'equipment') {
        throw new Error('Este item no se puede equipar');
      }
      
      // Equip item
      const equippedItem = inventoryService.equipItem(slotIndex);
      
      // Play sound effect
      await playEquipSound();
      
      // Show success notification
      this.$store.app.addNotification(
        `Equipado: ${equippedItem.name}`,
        'success'
      );
      
      this.loadInventory();
      
    } catch (error) {
      console.error('Failed to equip item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },
  
  // Unequip item
  async unequipItem(equipSlot) {
    if (this.loading || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const equipped = inventoryService.unequipItem(equipSlot);
      const itemData = inventoryService.getItemData(equipped.itemId);
      
      // Play sound effect
      await playEquipSound();
      
      // Show success notification
      this.$store.app.addNotification(
        `Desequipado: ${itemData.name}`,
        'info'
      );
      
      this.loadInventory();
      
    } catch (error) {
      console.error('Failed to unequip item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },
  
  // Sell item (with confirmation)
  confirmSell(slotIndex) {
    const slot = this.slots[slotIndex];
    if (!slot) return;
    
    const itemData = this.getItemData(slot);
    if (!itemData) return;
    
    const sellPrice = this.calculateSellPrice(itemData.value, slot.quantity);
    const marketChange = this.marketFactors ? Math.round((this.marketFactors.marketFactor - 1) * 100) : 0;
    
    this.selectedSlot = slotIndex;
    this.confirmAction = {
      type: 'sell',
      title: 'Vender Item',
      message: `¿Vender ${slot.quantity}x ${itemData.name} por ${sellPrice} monedas?`,
      subtitle: marketChange !== 0 ? `Mercado: ${marketChange >= 0 ? '+' : ''}${marketChange}%` : '',
      confirmText: 'Vender',
      confirmClass: 'btn-success'
    };
    this.showConfirmDialog = true;
  },

  // Execute sell
  async sellItem() {
    if (this.selectedSlot === null || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const slotIndex = this.selectedSlot;
      const slot = this.slots[slotIndex];
      const itemData = this.getItemData(slot);
      
      // Sell item via API
      const response = await this.$store.api.post('/inventory/sell', {
        itemId: slot.itemId,
        quantity: slot.quantity
      });
      
      // Play sound effect
      await playUseSound();
      
      // Show success notification
      this.$store.app.addNotification(response.message, 'success');
      
      // Update player coins
      this.$store.app.updatePlayer(response.player);
      
      this.loadInventory();
      this.closeConfirmDialog();
      
    } catch (error) {
      console.error('Failed to sell item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },

  // Repair item (with confirmation)
  confirmRepair(slotIndex) {
    const slot = this.slots[slotIndex];
    if (!slot) return;
    
    const itemData = this.getItemData(slot);
    if (!itemData || !itemData.maxDurability) return;
    
    const currentDurability = slot.durability || itemData.maxDurability || 100;
    const maxDurability = itemData.maxDurability || 100;
    
    if (currentDurability >= maxDurability) {
      this.$store.app.addNotification('Este item ya está en perfectas condiciones', 'info');
      return;
    }
    
    const repairPoints = maxDurability - currentDurability;
    const repairCost = Math.ceil(itemData.value * 0.05) * repairPoints;
    
    this.selectedSlot = slotIndex;
    this.confirmAction = {
      type: 'repair',
      title: 'Reparar Item',
      message: `¿Reparar ${itemData.name} (+${repairPoints} durabilidad) por ${repairCost} monedas?`,
      subtitle: `Durabilidad: ${currentDurability}/${maxDurability}`,
      confirmText: 'Reparar',
      confirmClass: 'btn-warning'
    };
    this.showConfirmDialog = true;
  },

  // Execute repair
  async repairItem() {
    if (this.selectedSlot === null || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const slotIndex = this.selectedSlot;
      const slot = this.slots[slotIndex];
      const itemData = this.getItemData(slot);
      
      const currentDurability = slot.durability || itemData.maxDurability || 100;
      const maxDurability = itemData.maxDurability || 100;
      const repairPoints = maxDurability - currentDurability;
      
      // Repair item via API
      const response = await this.$store.api.post('/inventory/repair', {
        itemId: slot.itemId,
        repairPoints: repairPoints
      });
      
      // Play sound effect
      await playEquipSound();
      
      // Show success notification
      this.$store.app.addNotification(response.message, 'success');
      
      // Update player coins
      this.$store.app.updatePlayer(response.player);
      
      this.loadInventory();
      this.closeConfirmDialog();
      
    } catch (error) {
      console.error('Failed to repair item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },

  // Calculate sell price with market factor
  calculateSellPrice(baseValue, quantity = 1) {
    if (!this.marketFactors) return Math.floor(baseValue * quantity * 0.7); // Default 70%
    return Math.floor(baseValue * quantity * this.marketFactors.marketFactor);
  },
  
  // Discard item (with confirmation)
  confirmDiscard(slotIndex) {
    const slot = this.slots[slotIndex];
    if (!slot) return;
    
    const itemData = this.getItemData(slot);
    if (!itemData) return;
    
    this.selectedSlot = slotIndex;
    this.confirmAction = {
      type: 'discard',
      title: 'Desechar Item',
      message: `¿Estás seguro de que quieres desechar ${slot.quantity}x ${itemData.name}?`,
      confirmText: 'Desechar',
      confirmClass: 'btn-danger'
    };
    this.showConfirmDialog = true;
  },
  
  // Execute discard
  async discardItem() {
    if (this.selectedSlot === null || this.isOffline) return;
    
    try {
      this.loading = true;
      this.error = null;
      
      const slotIndex = this.selectedSlot;
      const slot = this.slots[slotIndex];
      const itemData = this.getItemData(slot);
      
      // Remove item
      const removedQuantity = inventoryService.removeItem(slotIndex);
      
      // Play sound effect
      await playDiscardSound();
      
      // Show success notification
      this.$store.app.addNotification(
        `Desechado: ${removedQuantity}x ${itemData.name}`,
        'warning'
      );
      
      this.loadInventory();
      this.closeConfirmDialog();
      
    } catch (error) {
      console.error('Failed to discard item:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
      await playErrorSound();
    } finally {
      this.loading = false;
    }
  },
  
  // Close confirmation dialog
  closeConfirmDialog() {
    this.showConfirmDialog = false;
    this.confirmAction = null;
    this.selectedSlot = null;
  },
  
  // Execute confirmed action
  async executeConfirmedAction() {
    if (!this.confirmAction) return;
    
    switch (this.confirmAction.type) {
      case 'sell':
        await this.sellItem();
        break;
      case 'repair':
        await this.repairItem();
        break;
      case 'discard':
        await this.discardItem();
        break;
      default:
        console.warn('Unknown confirm action:', this.confirmAction.type);
    }
  },
  
  // Get slot styling based on item rarity
  getSlotClass(slot) {
    if (!slot) return 'inventory-slot inventory-slot-empty';
    
    const itemData = this.getItemData(slot);
    if (!itemData) return 'inventory-slot';
    
    const rarity = inventoryService.getRarityData(itemData.rarity);
    return `inventory-slot inventory-slot-filled rarity-${itemData.rarity}`;
  },
  
  // Get equipped slot styling
  getEquippedSlotClass(equipSlot) {
    const equipped = this.equipped[equipSlot];
    if (!equipped) return 'equipped-slot equipped-slot-empty';
    
    const itemData = inventoryService.getItemData(equipped.itemId);
    if (!itemData) return 'equipped-slot';
    
    return `equipped-slot equipped-slot-filled rarity-${itemData.rarity}`;
  },
  
  // Get stat name in Spanish
  getStatName(stat) {
    const statNames = {
      health: 'Salud',
      thirst: 'Sed',
      hunger: 'Hambre',
      mana: 'Maná',
      damage: 'Daño',
      defense: 'Defensa'
    };
    
    return statNames[stat] || stat;
  },
  
  // Get equipment slot name
  getEquipSlotName(equipSlot) {
    const slotNames = {
      weapon: 'Arma',
      armor: 'Armadura',
      accessory: 'Accesorio'
    };
    
    return slotNames[equipSlot] || equipSlot;
  },
  
  // Check if item can be used
  canUseItem(slot) {
    if (!slot) return false;
    const itemData = this.getItemData(slot);
    return itemData && itemData.category === 'consumable';
  },
  
  // Check if item can be equipped
  canEquipItem(slot) {
    if (!slot) return false;
    const itemData = this.getItemData(slot);
    return itemData && itemData.category === 'equipment';
  },

  // Check if item can be sold
  canSellItem(slot) {
    if (!slot) return false;
    const itemData = this.getItemData(slot);
    return itemData && itemData.value > 0;
  },

  // Check if item can be repaired
  canRepairItem(slot) {
    if (!slot) return false;
    const itemData = this.getItemData(slot);
    if (!itemData || !itemData.maxDurability) return false;
    
    const currentDurability = slot.durability || itemData.maxDurability || 100;
    const maxDurability = itemData.maxDurability || 100;
    
    return currentDurability < maxDurability;
  },
  
  // Get weight percentage
  getWeightPercentage() {
    return Math.round((this.stats.currentWeight / this.stats.maxWeight) * 100);
  },
  
  // Get weight bar color
  getWeightBarColor() {
    const percentage = this.getWeightPercentage();
    if (percentage >= 90) return '#ef4444'; // Red
    if (percentage >= 75) return '#f59e0b'; // Orange
    if (percentage >= 50) return '#eab308'; // Yellow
    return '#10b981'; // Green
  },
  
  // Refresh inventory
  refresh() {
    this.loadInventory();
  },
  
  // Cache inventory for offline use
  async cacheInventory(inventoryData) {
    try {
      if ('SimpleReadCache' in window) {
        await window.SimpleReadCache.set('inventory', inventoryData, { ttl: 300000 }); // 5 min TTL
      }
    } catch (error) {
      console.warn('Failed to cache inventory:', error);
    }
  },
  
  // Get cached inventory
  async getCachedInventory() {
    try {
      if ('SimpleReadCache' in window) {
        return await window.SimpleReadCache.get('inventory');
      }
    } catch (error) {
      console.warn('Failed to get cached inventory:', error);
    }
    return null;
  }
});